import { useMemo } from "react";
import { Wallet } from "lucide-react";
import MetricCard from "../ui/MetricCard";
import { computeClientBudgetHeld } from "../../lib/calculations";
import { formatUSD } from "../../lib/formatters";

// Pass-through client-budget ledger:
//   collected = portion of paid payments tagged as amountClientBudget
//   spent     = expenses tagged costType='client_budget'
//   held      = collected - spent  (the running liability owed to clients)
//
// Renders nothing if no client budget has ever been collected (so this only
// shows up after the user starts using the payment split feature).
export default function ClientBudgetCard({ state }) {
  const { collected, spent, held } = useMemo(
    () => computeClientBudgetHeld(state),
    [state]
  );

  if (collected <= 0 && spent <= 0) return null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Wallet size={14} className="text-purple-600 dark:text-purple-400" />
        <h3 className="font-display font-semibold text-sm text-brand-ink dark:text-[var(--text-primary)]">
          Client budgets held (pass-through)
        </h3>
        <span className="text-[11px] text-brand-slate">
          Money collected from clients to spend on their behalf — not your revenue.
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MetricCard
          label="Collected"
          value={formatUSD(collected)}
          accent="default"
          sublabel="client-budget portion of payments"
        />
        <MetricCard
          label="Spent on behalf"
          value={formatUSD(spent)}
          accent="default"
          sublabel="expenses tagged 'Client budget'"
        />
        <MetricCard
          label="Still held"
          value={formatUSD(held)}
          accent={held > 0 ? "amber" : held < 0 ? "red" : "green"}
          sublabel={
            held > 0
              ? "owed to clients (to spend)"
              : held < 0
              ? "overspent — investigate"
              : "fully spent"
          }
        />
      </div>
    </div>
  );
}
